import { Component, OnChanges, Input, ViewEncapsulation, output, inject } from '@angular/core';
import {
  GridColumnV2,
  GridStateV2,
  GridStateChangeV2,
  MultiselectV2SimpleItemSource,
  GridMultiselectFilterV2,
  GridUtilsV2,
  UserRightsService,
  ReactiveComponent,
  GridV2Module,
  IconV2Module,
} from '@schenck/rotec-components';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import {
  createSchedulingFilter,
  gridSchedulingMultiselectFilter,
} from 'src/components/task-interval.pipe';
import { UserRightsActions } from 'src/utils/enums/user-rights.enum';
import { filter, takeUntil } from 'rxjs/operators';
import { AsyncPipe } from '@angular/common';
import { TaskIntervalPipe } from '../../../components/task-interval.pipe';

export interface TaskItem {
  id: string;
  title: string;
  scheduling: any;
  responsible: string;
}

@Component({
  selector: 'ma-task-list',
  templateUrl: './task-list.component.html',
  styleUrls: ['./task-list.component.less'],
  encapsulation: ViewEncapsulation.None,
  imports: [GridV2Module, IconV2Module, AsyncPipe, TranslatePipe, TaskIntervalPipe],
})
export class TaskListComponent extends ReactiveComponent implements OnChanges {
  private readonly translate = inject(TranslateService);
  public userRightsService = inject(UserRightsService);

  @Input()
  public tasks: TaskItem[];
  public readonly openTask = output<string>();
  public readonly deleteTask = output<string>();
  public actions = UserRightsActions;

  public columns: GridColumnV2[] = this.createColumns();
  public searchState: GridStateV2 = {
    filter: [],
    fulltext: '',
    page: 0,
    results: [],
    sort: [],
    total: 0,
    loading: true,
    type: null,
  };

  constructor() {
    super();
  }

  public ngOnChanges(): void {
    this.columns = this.createColumns();
    this.search({});
  }

  public search(state: Partial<GridStateChangeV2>): void {
    const updatedSearchState = {
      ...this.searchState,
      loading: false,
      ...state,
    };

    this.searchState = {
      ...updatedSearchState,
      ...GridUtilsV2.getResults(this.tasks, {
        ...updatedSearchState,
        filterFnMap: {
          responsible: GridUtilsV2.multiselectFilter,
          title: GridUtilsV2.multiselectFilter,
          scheduling: gridSchedulingMultiselectFilter,
        },
        resultComparator: GridUtilsV2.defaultResultComparator,
      }),
    };
  }

  public _deleteTask(event: Event, id: string): void {
    event.stopPropagation();
    this.deleteTask.emit(id);
  }

  public _openTask(id: string, event?: Event): void {
    event && event.stopPropagation();

    this.userRightsService
      .hasActionRights$(UserRightsActions.updateTask)
      .pipe(
        takeUntil(this.onDestroy$),
        filter((actions) => typeof actions === 'boolean')
      )
      .subscribe((hasUpdateRights) => {
        if (hasUpdateRights) {
          this.openTask.emit(id);
        }
      });
  }

  private createColumns(): GridColumnV2[] {
    return [
      {
        key: 'title',
        sortable: true,
        width: '35%',
        filter: this.createMultiselectFilter('title'),
        notReorderable: true,
        notResizable: true,
        title: 'Title',
      },
      {
        key: 'scheduling',
        sortable: true,
        width: '25%',
        filter: createSchedulingFilter(this.tasks || [], this.translate),
        notReorderable: true,
        notResizable: true,
        title: 'Interval',
      },
      {
        key: 'responsible',
        sortable: true,
        width: '25%',
        filter: this.createMultiselectFilter('responsible'),
        notReorderable: true,
        notResizable: true,
        title: 'Task Responsible',
      },
      {
        key: 'actions',
        notReorderable: true,
        notResizable: true,
        sortable: false,
        notConfigurable: true,
        title: '',
        width: '9%',
      },
    ];
  }

  private createMultiselectFilter(key: keyof TaskItem): GridMultiselectFilterV2 {
    return {
      type: 'multiselect',
      items: new MultiselectV2SimpleItemSource(
        (this.tasks || []).map((task) => task[key]).filter((val) => !!val)
      ),
    };
  }
}
